import useTranslator from "../hooks/useTranslator";
import { useContext, useState, useEffect } from "react";
import LandLordContext from "../context/landlord/LandLordContext";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "../helper/common";
import Loader from "./Loader";

const TopUpCreditModal = ({ open, setOpen }) => {
  const navigate = useNavigate();
  const { _t } = useTranslator();
  const { addCredit, getPricingPlans } = useContext(LandLordContext);
  const { workspaceId } = useParams();

  const [buttonDisabled, setButtonDisabled] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [autoTopUp, setAutoTopUp] = useState(false);
  const [creditPlans, setCreditPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchPricingPlans = async () => {
    try {
      setLoading(true);
      const response = await getPricingPlans();
      const data = response?.data || response;
      if (data) {
        const formattedPlans = data.map((plan) => ({
          id: plan.id || plan._id,
          credits: plan.credits,
          views: plan.views,
          pricePerProfile: plan.pricePerProfile,
          price: plan.price,
          badge: plan.badge ? _t("mostPopular") : null,
          isEnterprise: plan.isEnterprise || plan.type === "enterprise",
        }));
        setCreditPlans(formattedPlans);
        if (formattedPlans.length > 0 && !selectedPlan) {
          setSelectedPlan(formattedPlans[0].id);
        }
      }
    } catch (err) {
      console.log(err);
      toast.error("Error while fetching pricing plans");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchPricingPlans();
    }
  }, [open]);

  const handleAddCredit = () => {
    setButtonDisabled(true);
    const selectedPlanData = creditPlans.find(
      (plan) => plan.id === selectedPlan
    );
    addCredit({ planId: selectedPlan, autoTopUp })
      .then((res) => {
        const data = res.success_res;
        window.location.replace(data?.paymentUrl);
      })
      .catch((err) => {
        console.log(err);
        toast.error("Error while adding credits");
      })
      .finally(() => {
        setButtonDisabled(false);
      });
  };

  const handleCreditHistory = () => {
    navigate(`/${workspaceId}/credits-ledger`);
    setOpen();
  };

  const handleContactSales = () => {
    window.open("https://skortorent.com/?chat", "_blank");
  };

  const filteredPlans = autoTopUp
    ? creditPlans.filter((plan) => !plan.isEnterprise)
    : creditPlans;

  return (
    open && (
      <div
        className={`modal wrapper-modal fade top-up-credit-modal top-up-credit-modal--open ${
          open ? "show d-block" : ""
        }`}
        onClick={setOpen}
      >
        <div
          className="modal-dialog"
          role="document"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="modal-content top-up-credit-modal__content">
            <button
              type="button"
              className="top-up-credit-modal__close"
              onClick={setOpen}
              aria-label="Close"
            />

            <div className="top-up-credit-modal__hero">
              <div className="top-up-credit-modal__coin" aria-hidden="true">
                <img src="/images/credit-point1.svg" />
              </div>
              <h5 className="top-up-credit-modal__title">
                {_t("topUpCredits")}
              </h5>
              <p className="top-up-credit-modal__subtitle">
                5 {_t("creditsWillBeUsedToViewEachTenantProfile")}
              </p>
            </div>

            {loading ? (
              <div
                className="top-up-credit-modal__loading"
                style={{ position: "relative" }}
              >
                <Loader />
              </div>
            ) : (
              <div className="top-up-credit-modal__body">
                <div className="top-up-credit-modal__plans">
                  {filteredPlans.map((plan) => {
                    const isSelected = selectedPlan === plan.id;

                    return (
                      <button
                        key={plan.id}
                        type="button"
                        className={`top-up-credit-modal__plan ${
                          isSelected
                            ? "top-up-credit-modal__plan--selected"
                            : ""
                        }`}
                        onClick={() => setSelectedPlan(plan.id)}
                      >
                        {plan.badge && (
                          <span className="top-up-credit-modal__badge">
                            {plan.badge}
                          </span>
                        )}
                        <span
                          className={`top-up-credit-modal__radio ${
                            isSelected
                              ? "top-up-credit-modal__radio--selected"
                              : ""
                          }`}
                        />
                        <span className="top-up-credit-modal__plan-copy">
                          <strong className="top-up-credit-modal__plan-title">
                            {plan.credits} {_t("credits")}
                          </strong>
                          <span className="top-up-credit-modal__plan-meta">
                            x{plan.views} {_t("profileViews")} ·{" "}
                            {plan.pricePerProfile}/{_t("perProfile")}
                          </span>
                        </span>
                        <strong className="top-up-credit-modal__plan-price">
                          € {plan.price}
                        </strong>
                      </button>
                    );
                  })}

                  {!autoTopUp ? (
                    <button
                      type="button"
                      className="top-up-credit-modal__plan top-up-credit-modal__plan--enterprise"
                      onClick={handleContactSales}
                    >
                      <span className="top-up-credit-modal__radio" />
                      <span className="top-up-credit-modal__plan-copy">
                        <strong className="top-up-credit-modal__plan-title">
                          {_t("enterprise")}
                        </strong>
                        <span className="top-up-credit-modal__plan-meta">
                          {_t("customVolume")} ·{" "}
                          <span className="top-up-credit-modal__sales-link">
                            {_t("contactSales")}
                          </span>
                        </span>
                      </span>
                      <span className="top-up-credit-modal__arrow">→</span>
                    </button>
                  ) : null}
                </div>

                {/* auto-pay button */}
                <div className="top-up-credit-modal__auto-top-up">
                  <div className="top-up-credit-modal__auto-copy">
                    <strong>{_t("autoTopUp")}</strong>
                    <span>{_t("autoTopUpDescription")}</span>
                  </div>
                  <button
                    type="button"
                    className={`top-up-credit-modal__switch ${
                      autoTopUp ? "top-up-credit-modal__switch--active" : ""
                    }`}
                    onClick={() =>
                      setAutoTopUp((currentValue) => !currentValue)
                    }
                    aria-pressed={autoTopUp}
                    aria-label="Toggle auto top-up"
                  >
                    <span className="top-up-credit-modal__switch-thumb" />
                  </button>
                </div>

                <div className="top-up-credit-modal__actions">
                  <button
                    type="button"
                    onClick={handleCreditHistory}
                    className="top-up-credit-modal__button top-up-credit-modal__button--secondary"
                  >
                    {_t("creditHistory")}
                  </button>
                  <button
                    type="button"
                    onClick={handleAddCredit}
                    className="top-up-credit-modal__button top-up-credit-modal__button--primary"
                    disabled={buttonDisabled || loading}
                  >
                    {buttonDisabled ? _t("loading") : _t("buy")}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  );
};

export default TopUpCreditModal;
